import type { TaskSurfaceCard } from "@/services/api/agentic-v2/types";
import { reviewQueueClientName, reviewQueueTitle } from "./reviewQueueModel";

export type NeedsYouRiskTier = "high" | "medium" | "low";

export type NeedsYouRiskFilter = "all" | NeedsYouRiskTier;
export type NeedsYouSortColumn = "risk" | "status" | "subtask" | "run_client" | "waiting";
export type NeedsYouSortDirection = "asc" | "desc";

/** Lifecycle status shown on record-page task lists (lead/client detail). */
export type NeedsYouTaskStatus = "needs_you" | "open" | "blocked" | "done";

export type NeedsYouRow = {
  task: TaskSurfaceCard;
  risk: NeedsYouRiskTier;
  status: NeedsYouTaskStatus;
  subtaskTitle: string;
  runTitle: string;
  clientName: string;
  workerLabel: string;
  whyPaused: string;
  waitingMinutes: number | null;
};

/** Higher number is more urgent. Descending sort puts the highest risk first. */
const RISK_SEVERITY: Record<NeedsYouRiskTier, number> = {
  high: 2,
  medium: 1,
  low: 0,
};

/** Higher number needs attention sooner. Descending sort puts action-needed first, done last. */
const TASK_STATUS_SEVERITY: Record<NeedsYouTaskStatus, number> = {
  needs_you: 3,
  blocked: 2,
  open: 1,
  done: 0,
};

/** Lifecycle status for a task row — done when terminal, needs-you when awaiting human review. */
export function taskConsoleStatus(task: TaskSurfaceCard): NeedsYouTaskStatus {
  const status = (task.status ?? "").toLowerCase();
  if (["done", "completed", "complete", "approved", "auto_approved"].includes(status)) {
    return "done";
  }
  if (status === "needs_review" || task.requires_review) return "needs_you";
  if (status === "blocked" || status === "failed" || task.gate_blocked_reason) {
    return "blocked";
  }
  return "open";
}

export function taskStatusLabel(status: NeedsYouTaskStatus): string {
  switch (status) {
    case "needs_you":
      return "Needs you";
    case "blocked":
      return "Blocked";
    case "done":
      return "Done";
    default:
      return "Open";
  }
}

export function normalizeRisk(task: TaskSurfaceCard): NeedsYouRiskTier {
  const tier = task.risk_tier?.toLowerCase();
  if (tier === "high" || tier === "critical") return "high";
  if (tier === "medium" || tier === "med") return "medium";
  if (tier === "low") return "low";
  if (task.confidence === "low" || task.blocking_someone) return "high";
  if (task.confidence === "medium") return "medium";
  return "low";
}

export function whyPaused(task: TaskSurfaceCard): string {
  const gateTitle = task.gate_blocked_display?.title?.trim();
  if (gateTitle) return gateTitle;
  const gateBody = task.gate_blocked_display?.body?.trim();
  if (gateBody) return gateBody;
  if (task.disposition_reasoning?.trim()) return task.disposition_reasoning.trim();
  if (task.confidence === "low") {
    return "Confidence is low — review required before proceeding";
  }
  if (task.confidence === "medium") {
    return "Confidence is medium — human judgement requested";
  }
  if (task.presentation?.body?.trim()) return task.presentation.body.trim();
  if (task.latest_summary?.trim()) return task.latest_summary.trim();
  return "Paused for human review";
}

export function workerLabel(task: TaskSurfaceCard): string {
  if (task.owner_role?.trim()) return task.owner_role.trim();
  if (task.kit_slug?.trim()) return task.kit_slug.replace(/_/g, " ");
  return "Agent";
}

function waitingMinutes(task: TaskSurfaceCard): number | null {
  if (task.age_hours != null) return Math.round(task.age_hours * 60);
  const iso = task.queued_at ?? task.updated_at;
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.floor(ms / 60_000));
}

function formatWaiting(minutes: number | null): string {
  if (minutes == null) return "—";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
  }
  const rem = minutes % 60;
  return rem > 0 ? `${hours}h ${String(rem).padStart(2, "0")}m` : `${hours}h`;
}

export function buildNeedsYouRows(tasks: TaskSurfaceCard[]): NeedsYouRow[] {
  return tasks.map((task) => ({
    task,
    risk: normalizeRisk(task),
    status: taskConsoleStatus(task),
    subtaskTitle: reviewQueueTitle(task),
    runTitle: task.mission?.name?.trim() || "Mission run",
    clientName: reviewQueueClientName(task) || "Internal",
    workerLabel: workerLabel(task),
    whyPaused: whyPaused(task),
    waitingMinutes: waitingMinutes(task),
  }));
}

export function sortNeedsYouRows(rows: NeedsYouRow[]): NeedsYouRow[] {
  return sortNeedsYouRowsByColumn(rows, "risk", "desc");
}

export function filterNeedsYouTasks(
  tasks: TaskSurfaceCard[],
  searchQuery: string,
  riskFilter: NeedsYouRiskFilter,
): TaskSurfaceCard[] {
  const needle = searchQuery.trim().toLowerCase();
  return tasks.filter((task) => {
    if (riskFilter !== "all" && normalizeRisk(task) !== riskFilter) return false;
    if (!needle) return true;
    const [row] = buildNeedsYouRows([task]);
    const haystack = [
      row.subtaskTitle,
      row.runTitle,
      row.clientName,
      row.workerLabel,
      row.whyPaused,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(needle);
  });
}

export function sortNeedsYouRowsByColumn(
  rows: NeedsYouRow[],
  column: NeedsYouSortColumn,
  direction: NeedsYouSortDirection,
): NeedsYouRow[] {
  const sorted = [...rows].sort((left, right) => {
    let cmp = 0;
    switch (column) {
      case "risk":
        cmp = RISK_SEVERITY[left.risk] - RISK_SEVERITY[right.risk];
        break;
      case "status":
        cmp = TASK_STATUS_SEVERITY[left.status] - TASK_STATUS_SEVERITY[right.status];
        break;
      case "subtask":
        cmp = left.subtaskTitle.localeCompare(right.subtaskTitle);
        break;
      case "run_client": {
        const leftKey = `${left.runTitle} ${left.clientName}`.toLowerCase();
        const rightKey = `${right.runTitle} ${right.clientName}`.toLowerCase();
        cmp = leftKey.localeCompare(rightKey);
        break;
      }
      case "waiting":
        cmp = (left.waitingMinutes ?? 0) - (right.waitingMinutes ?? 0);
        break;
    }
    return direction === "desc" ? -cmp : cmp;
  });
  return sorted;
}

export function defaultSortDirection(column: NeedsYouSortColumn): NeedsYouSortDirection {
  if (column === "subtask" || column === "run_client") {
    return "asc";
  }
  return "desc";
}

export function riskLabel(risk: NeedsYouRiskTier): string {
  return risk.charAt(0).toUpperCase() + risk.slice(1);
}

export { formatWaiting };
